import { useState, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'sonner';
import { Upload, X, CheckCircle2, AlertCircle, Loader2, Download, Users } from 'lucide-react';
import * as XLSX from 'xlsx';

const BulkUserUpload = ({ organizationId, departments = [], onSuccess, onClose }) => {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const parseFile = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });

        const normalized = raw.map((row, i) => {
          // Normalizar nombres de columnas (mayús/minús, espacios, tildes)
          const normalize = (s) => s?.toLowerCase()
            .replaceAll(/\s+/g, '_')
            .normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '');

          const get = (keys) => {
            for (const k of Object.keys(row)) {
              if (keys.includes(normalize(k))) return String(row[k]).trim();
            }
            return '';
          };

          const nombre = get(['nombre']);
          const apellido = get(['apellido', 'apellidos', 'primer_apellido']);
          const fullName = apellido ? `${nombre} ${apellido}` : nombre;
          const dni = get(['dni', 'nif']);
          const ss = get(['num_seguridad_social','seguridad_social','nss','numero_seguridad_social','ss']);
          const email = get(['email', 'correo', 'correo_electronico']);
          const dept = get(['departamento', 'department', 'depto']);

          const deptObj = departments.find(
            d => d.name.toLowerCase() === dept.toLowerCase()
          );

          const errors = [];
          if (!fullName.trim()) errors.push('Nombre requerido');
          if (!dni) errors.push('DNI requerido');
          if (!ss) errors.push('Nº SS requerido');

          return {
            _row: i + 2,
            name: fullName,
            dni,
            social_security: ss,
            email: email || null,
            department: deptObj?.id || null,
            department_name: dept,
            errors,
            status: errors.length ? 'error' : 'ready',
          };
        });

        setRows(normalized);
        setResult(null);
      } catch (err) {
        toast.error('Error al leer el archivo: ' + err.message);
      }
    };
    reader.readAsBinaryString(file);
  }, [departments]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) parseFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) parseFile(f);
  };

  const validRows = rows.filter(r => r.status === 'ready');
  const errorRows = rows.filter(r => r.status === 'error');

  const handleImport = async () => {
    if (!validRows.length) return;
    setImporting(true);
    try {
      const res = await api.post('/superadmin/users/bulk-create', {
        organization_id: organizationId,
        users: validRows.map(r => ({
          name: r.name,
          dni: r.dni,
          social_security: r.social_security,
          email: r.email || null,
          department: r.department || null,
        })),
      });
      setResult(res.data);
      toast.success(`✅ ${res.data.created} usuarios creados correctamente`);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la importación');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['nombre', 'apellido', 'dni', 'num_seguridad_social', 'email', 'departamento'],
      ['Ana', 'García López', '12345678A', '28/123456/78', 'ana@empresa.com', 'Ventas'],
      ['Carlos', 'Ruiz Mora', '87654321B', '28/654321/90', '', ''],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
    XLSX.writeFile(wb, 'plantilla_usuarios.xlsx');
  };

  return (
    <div className="space-y-5">
      {/* Botón plantilla */}
      <button
        onClick={downloadTemplate}
        className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-[#00FFFF]/70 hover:text-[#00FFFF] transition-colors"
      >
        <Download className="w-3.5 h-3.5" /> Descargar plantilla Excel
      </button>

      {/* Drop zone */}
      {!rows.length && (
        <label
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          className="flex flex-col items-center justify-center gap-3 p-10 border-2 border-dashed border-[#00FFFF]/30 bg-[#00FFFF]/5 rounded-xl cursor-pointer hover:border-[#00FFFF] hover:bg-[#00FFFF]/10 transition-all"
        >
          <Upload className="w-8 h-8 text-[#00FFFF]/50" />
          <div className="text-center">
            <p className="text-[13px] font-bold text-white mb-1">Arrastra tu archivo o haz clic para seleccionar</p>
            <p className="text-[11px] text-white/40 uppercase tracking-widest">.xlsx · .xls · .csv · Máx. 1000 filas</p>
          </div>
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
        </label>
      )}

      {/* Tabla de previsualización */}
      {rows.length > 0 && !result && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-black uppercase tracking-widest text-white/60">
              {rows.length} filas detectadas ·{' '}
              <span className="text-[#ccff00]">{validRows.length} válidas</span>
              {errorRows.length > 0 && <span className="text-red-400"> · {errorRows.length} con errores</span>}
            </p>
            <button onClick={() => setRows([])} className="text-white/30 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="border border-white/10 rounded-xl overflow-hidden max-h-64 overflow-y-auto">
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-black/60">
                <tr className="border-b border-white/10">
                  {['Nombre', 'DNI', 'N.º SS', 'Email', 'Depto.', ''].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] font-black uppercase tracking-widest text-white/40">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {rows.map((r, i) => (
                  <tr key={i} className={r.status === 'error' ? 'bg-red-500/5' : 'hover:bg-white/[0.02]'}>
                    <td className="px-3 py-2 text-white font-medium">{r.name || <span className="text-red-400">—</span>}</td>
                    <td className="px-3 py-2 font-mono text-white/80">{r.dni || <span className="text-red-400">—</span>}</td>
                    <td className="px-3 py-2 font-mono text-white/60">{r.social_security || <span className="text-red-400">—</span>}</td>
                    <td className="px-3 py-2 text-white/40">{r.email || '—'}</td>
                    <td className="px-3 py-2 text-white/40">{r.department_name || '—'}</td>
                    <td className="px-3 py-2">
                      {r.status === 'ready'
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-[#ccff00]" />
                        : <span title={r.errors.join(', ')}><AlertCircle className="w-3.5 h-3.5 text-red-400" /></span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {errorRows.length > 0 && (
            <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-red-300">Las filas con errores serán ignoradas. Solo se importarán las {validRows.length} filas válidas.</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2 border-t border-white/10">
            <button onClick={() => setRows([])} className="px-5 py-2.5 border border-white/20 text-white rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-white/5 transition-colors">
              Cancelar
            </button>
            <button
              onClick={handleImport}
              disabled={importing || !validRows.length}
              className="px-8 py-2.5 btn-neon-cyan rounded-xl text-[11px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
            >
              {importing
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Importando...</>
                : <><Users className="w-4 h-4" /> Importar {validRows.length} usuarios</>
              }
            </button>
          </div>
        </>
      )}

      {/* Resultado */}
      {result && (
        <div className="space-y-4">
          <div className="p-5 bg-[#ccff00]/5 border border-[#ccff00]/30 rounded-xl">
            <p className="text-[13px] font-black text-[#ccff00] uppercase tracking-wider mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" /> Importación completada
            </p>
            <div className="grid grid-cols-3 gap-3 text-center text-[11px]">
              <div className="p-3 bg-black/40 rounded-xl">
                <p className="text-2xl font-black text-[#ccff00]">{result.created}</p>
                <p className="text-white/50 uppercase tracking-widest mt-1">Creados</p>
              </div>
              <div className="p-3 bg-black/40 rounded-xl">
                <p className="text-2xl font-black text-yellow-400">{result.duplicates}</p>
                <p className="text-white/50 uppercase tracking-widest mt-1">Duplicados</p>
              </div>
              <div className="p-3 bg-black/40 rounded-xl">
                <p className="text-2xl font-black text-red-400">{result.errors}</p>
                <p className="text-white/50 uppercase tracking-widest mt-1">Errores</p>
              </div>
            </div>
          </div>

          {/* Lista de códigos generados */}
          {result.users?.length > 0 && (
            <div className="border border-white/10 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 bg-black/60">
                  <tr className="border-b border-white/10">
                    {['Nombre', 'Código de acceso', 'Estado'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-[10px] font-black uppercase tracking-widest text-white/40">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {result.users.map((u, i) => (
                    <tr key={i} className="hover:bg-white/[0.02]">
                      <td className="px-3 py-2 text-white">{u.name}</td>
                      <td className="px-3 py-2 font-mono text-[#00FFFF] tracking-widest">{u.access_code}</td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] bg-[#ccff00]/10 text-[#ccff00] border border-[#ccff00]/30 px-2 py-0.5 rounded uppercase tracking-widest">
                          {u.email_sent ? 'Email enviado' : 'Sin email'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button
            onClick={() => {
              // Exportar los códigos a Excel
              const ws = XLSX.utils.json_to_sheet(
                result.users.map(u => ({
                  Nombre: u.name,
                  'Código de acceso': u.access_code,
                  Email: u.email || '—',
                }))
              );
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Códigos');
              XLSX.writeFile(wb, 'codigos_acceso.xlsx');
            }}
            className="w-full flex items-center justify-center gap-2 py-2.5 btn-neon-lime rounded-xl text-[11px] font-black uppercase tracking-widest"
          >
            <Download className="w-4 h-4" /> Exportar códigos a Excel
          </button>
        </div>
      )}
    </div>
  );
};

export default BulkUserUpload;